// The booking sync form's calls, on the background-task API (`/v2/edit/bg_tasks/...`, all needing
// `ops.sync_bookings`; dashboard_sync.openapi.yaml names them in its description).
//
// Plain fetch through the sync page's caller (`../api` `createCaller`), not the data provider: a 403 here is
// "not your permission" (`bgtask_permission_required`), shown, never a sign-out; a 401 still signs out. It
// also keeps the franchise wrapper out: a licensee is named explicitly (`franchise_to` on the reads,
// `tour_operator` on the start), only when the form's Licensee picker chose one.
import { createCaller, useSessionClient } from '../api';
import type { ClientOptions } from '../api';
import type { SyncTask } from '../types';
import type {
    BookingSyncAccount, BookingSyncIntegration, ImportSource, StartSyncBody, SyncBookingsTarget,
} from './types';

export const BOOKING_SYNC_ENDPOINTS = {
    integrationList: { method: 'GET', path: '/v2/edit/bg_tasks/integration_list/' },
    integrationAccounts: { method: 'GET', path: '/v2/edit/bg_tasks/integration_accounts/' },
    importSources: { method: 'GET', path: '/v2/edit/bg_tasks/import_sources/' },
    syncTargets: { method: 'GET', path: '/v2/edit/bg_tasks/sync_targets/' },
    startSync: { method: 'POST', path: '/v2/edit/bg_tasks/start_sync/' },
} as const;

/** The query of a read: the licensee (when one is chosen) and the given parameters; empty ones are left out. */
const scoped = (franchiseTo: number | null, query: Record<string, string | null | undefined> = {}) => ({
    ...query,
    franchise_to: franchiseTo != null ? String(franchiseTo) : null,
});

export function createBookingSyncApi(options: ClientOptions) {
    const call = createCaller(BOOKING_SYNC_ENDPOINTS, options);

    return {
        /** The configured booking systems; `account` gives that account's extra arguments. */
        integrations: (franchiseTo: number | null, account: string | null = null, signal?: AbortSignal) =>
            call<BookingSyncIntegration[]>('integrationList', { query: scoped(franchiseTo, { account }), signal }),
        accounts: (integration: string, franchiseTo: number | null, signal?: AbortSignal) =>
            call<BookingSyncAccount[]>('integrationAccounts', { query: scoped(franchiseTo, { integration }), signal }),
        importSources: (franchiseTo: number | null, signal?: AbortSignal) =>
            call<ImportSource[]>('importSources', { query: scoped(franchiseTo), signal }),
        targets: (integration: string, account: string | null, franchiseTo: number | null, signal?: AbortSignal) =>
            call<{ results: SyncBookingsTarget[] }>('syncTargets', { query: scoped(franchiseTo, { integration, account }), signal }),
        /** Answers the started run (202 `{task_id, task_type, task_label, state}`). */
        start: (body: StartSyncBody) => call<SyncTask>('startSync', { json: body }),
    };
}

export type BookingSyncApi = ReturnType<typeof createBookingSyncApi>;

/** The booking sync calls for the signed-in session. */
export const useBookingSyncApi = (): BookingSyncApi => useSessionClient(createBookingSyncApi);
